import {type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import type {TFunction} from 'i18next';
import {AlertTriangle, Camera, Check, ImageIcon, Upload} from 'lucide-react';
import {Button, CaptureFrame, StatusBox, cn, type CaptureVariant} from '@chekinapp/ui';
import {STATUSES} from '@chekin/core';
import {CaptureCheckingView, type CaptureCheck} from './CaptureCheckingView';
import {CaptureDock} from './CaptureDock';
import {CaptureWaitNote} from './CaptureWaitNote';
import {OverlayRetryButton} from './OverlayRetryButton';
import {QuickFixes} from './QuickFixes';
import {useCaptureErrorRevealed} from './useCaptureErrorRevealed';
import {VerifyLaterLink} from './VerifyLaterLink';

type CaptureLabelOverrides = {
  scanningLabel?: ReactNode;
  successLabel?: ReactNode;
  errorTitle?: ReactNode;
  errorDescription?: ReactNode;
};

function getCaptureLabels(
  t: TFunction,
  isFace: boolean,
  overrides: CaptureLabelOverrides,
) {
  const {scanningLabel, successLabel, errorTitle, errorDescription} = overrides;

  return {
    scanLabel: scanningLabel ?? (isFace ? t('verification_in_progress') : t('scanning')),
    successText:
      successLabel ??
      (isFace ? t('selfie_captured_exclamation') : t('photo_taken_exclamation')),
    errorTitleText:
      errorTitle ??
      (isFace ? t('selfie_validation_failed') : t('document_validation_failed')),
    errorDescText:
      errorDescription ??
      (isFace
        ? t('selfie_validation_failed_text')
        : t('document_validation_failed_text')),
  };
}

function ScanningStatus({label}: {label: ReactNode}) {
  return (
    <CaptureFrame.Status>
      <CaptureFrame.Spinner />
      {label}
    </CaptureFrame.Status>
  );
}

export type CaptureChecking = {
  checks: CaptureCheck[];
  passedChecks?: CaptureCheck[];
  isFinalStep?: boolean;
};

export type CaptureProcessFrameProps = {
  variant?: CaptureVariant;
  checking?: CaptureChecking;
  state: STATUSES;
  image?: string | null;
  scanningLabel?: ReactNode;
  successLabel?: ReactNode;
  errorTitle?: ReactNode;
  errorDescription?: ReactNode;
  onRetry?: () => void;
  retryLabel?: ReactNode;
  retryIcon?: ReactNode;
  onRetake?: () => void;
  onUploadFile?: () => void;
  uploadLabel?: ReactNode;
  onVerifyLater?: () => void;
  isFromFile?: boolean;
  hasKnownCause?: boolean;
  isServiceFailure?: boolean;
  errorHelp?: ReactNode;
  className?: string;
};

export function CaptureProcessFrame({
  variant = 'document',
  checking,
  state,
  image,
  scanningLabel,
  successLabel,
  errorTitle,
  errorDescription,
  onRetry,
  retryLabel,
  retryIcon,
  onRetake,
  onUploadFile,
  uploadLabel,
  onVerifyLater,
  isFromFile,
  hasKnownCause,
  isServiceFailure,
  errorHelp,
  className,
}: CaptureProcessFrameProps) {
  const {t} = useTranslation('identity-verification');

  const isFace = variant === 'face';
  const isScanning = state === STATUSES.loading;
  const isSuccess = state === STATUSES.success;
  const isError = state === STATUSES.error;

  const isErrorRevealed = useCaptureErrorRevealed(isError);

  const frameStatus = isSuccess ? 'success' : isError ? 'error' : 'scanning';

  const {scanLabel, successText, errorTitleText, errorDescText} = getCaptureLabels(
    t,
    isFace,
    {scanningLabel, successLabel, errorTitle, errorDescription},
  );

  if (checking && !isErrorRevealed) {
    return (
      <CaptureCheckingView
        state={state}
        checks={checking.checks}
        passedChecks={checking.passedChecks}
        isFinalStep={checking.isFinalStep}
        isServiceFailure={isServiceFailure}
        className={className}
      />
    );
  }

  if (checking) {
    return (
      <div className={cn('iv-capture-error flex w-full flex-col gap-4', className)}>
        <StatusBox
          status="warning"
          title={errorTitleText}
          text={hasKnownCause ? errorDescText : t('photo_problem_generic')}
          icon={<AlertTriangle />}
          className="items-start rounded-[14px] px-3.5 py-3 [--status-box-icon-bg:transparent] [--status-box-icon-glyph-size:20px] [--status-box-icon-shadow:none] [--status-box-icon-size:20px] [--status-box-title-font-size:14px] [--status-box-title-line-height:20px] [--status-box-text-font-size:13px] [--status-box-text-line-height:18px]"
        />
        {errorHelp ??
          (!isServiceFailure && (
            <QuickFixes
              layout="tiles"
              variant={isFromFile ? 'file' : isFace ? 'face' : 'document'}
            />
          ))}
        <CaptureDock>
          <div className="iv-camera-capture__actions flex w-full flex-col items-center gap-2.5">
            {onRetry && (
              <Button
                onClick={onRetry}
                className="iv-camera-capture__retry w-full max-w-[340px] lg:w-[280px]"
              >
                {retryIcon}
                {retryLabel ?? t('retake_photo')}
              </Button>
            )}
            {onRetake && (
              <Button
                variant="outline"
                onClick={onRetake}
                className="w-full max-w-[340px] lg:w-[280px]"
              >
                <Camera />
                {t('take_another_photo')}
              </Button>
            )}
            {onUploadFile && (
              <Button
                variant="outline"
                onClick={onUploadFile}
                className="w-full max-w-[340px] lg:w-[280px]"
              >
                <ImageIcon />
                {uploadLabel ?? t('choose_from_gallery')}
              </Button>
            )}
            {onVerifyLater && <VerifyLaterLink onClick={onVerifyLater} />}
          </div>
        </CaptureDock>
      </div>
    );
  }

  return (
    <div className={cn('iv-camera-capture flex flex-col items-center gap-6', className)}>
      <CaptureFrame variant={variant} status={frameStatus}>
        <CaptureFrame.View>
          {image && (
            <img
              src={image}
              alt=""
              className={cn(
                'absolute inset-0 h-full w-full object-contain',
                isFace && 'scale-x-[-1]',
              )}
            />
          )}
          {isScanning && !isFace && <CaptureFrame.ScanLine active />}
        </CaptureFrame.View>

        {isScanning && <ScanningStatus label={scanLabel} />}

        {isSuccess && (
          <CaptureFrame.Status>
            <span
              aria-hidden
              className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[var(--chekin-green-700)] text-white"
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {t('capture_complete')}
          </CaptureFrame.Status>
        )}

        {isError && (
          <CaptureFrame.Status>
            <CaptureFrame.Dot tone="error" />
            {t('validation_failed')}
          </CaptureFrame.Status>
        )}

        {isSuccess && (
          <CaptureFrame.Overlay
            status="success"
            icon={<Check className="h-8 w-8" strokeWidth={3} />}
            title={successText}
          />
        )}

        {isError && (
          <CaptureFrame.Overlay
            status="error"
            icon={<AlertTriangle className="h-7 w-7" strokeWidth={2.5} />}
            title={errorTitleText}
            description={errorDescText}
            action={
              <div className="flex w-full flex-wrap items-center justify-center gap-2.5">
                {onRetry && (
                  <OverlayRetryButton
                    onClick={onRetry}
                    label={retryLabel}
                    icon={retryIcon}
                    className="iv-camera-capture__retry"
                  />
                )}
                {onRetake && (
                  <Button
                    onClick={onRetake}
                    className="h-11 gap-2 whitespace-nowrap border-none bg-white/20 px-5 text-[14px] font-semibold text-white hover:bg-white/30"
                  >
                    <Camera className="h-[18px] w-[18px] flex-none" />
                    {t('take_another_photo')}
                  </Button>
                )}
                {onUploadFile && (
                  <Button
                    onClick={onUploadFile}
                    className="h-11 gap-2 whitespace-nowrap border-none bg-white/20 px-5 text-[14px] font-semibold text-white hover:bg-white/30"
                  >
                    <Upload className="h-[18px] w-[18px] flex-none" />
                    {uploadLabel ?? t('upload_a_file')}
                  </Button>
                )}
              </div>
            }
          />
        )}
      </CaptureFrame>

      {isScanning && <CaptureWaitNote />}

      {isError && (
        <>
          {errorHelp}
          {!hasKnownCause && (
            <QuickFixes
              variant={isFromFile ? 'file' : isFace ? 'face' : 'document'}
              showUpload={Boolean(onUploadFile)}
            />
          )}

          {onVerifyLater && <VerifyLaterLink onClick={onVerifyLater} />}
        </>
      )}
    </div>
  );
}
